/**
 * Programmatic scrolling for the taskbar well's chevrons — their click, and a
 * wheel turned over one (the chevron is not inside the well, so the browser would
 * scroll nothing). Pure, so the arithmetic is tested without a browser.
 */

/** A wheel delta in lines (deltaMode 1, Firefox's mouse wheel) as pixels. */
export const WHEEL_LINE_PX = 16
/** A wheel step at least this big is a mouse notch, and animates; smaller ones are a trackpad's stream. */
export const WHEEL_NOTCH_PX = 50
/**
 * How long an animated step can still be under way — longer than the browser's
 * smooth scroll of a page. A step inside it, before the scroll has arrived,
 * continues that one rather than restarting from wherever it had got to.
 */
export const SCROLL_CHAIN_MS = 700

/** The last animated step: where it started, where it was headed, when it was asked for, and the last position seen on the way. */
export interface ScrollAim { from: number; top: number; at: number; last: number }

/**
 * A wheel event as pixels, or null when it is not a scroll to forward: ctrl+wheel
 * (a trackpad pinch arrives as one) is the browser's zoom, and a purely sideways
 * wheel moves nothing on a vertical list.
 */
export function wheelDeltaPx(e: { deltaY: number; deltaMode: number; ctrlKey: boolean }, clientHeight: number): number | null {
  if (e.ctrlKey || e.deltaY === 0) return null
  return e.deltaMode === 1 ? e.deltaY * WHEEL_LINE_PX : e.deltaMode === 2 ? e.deltaY * clientHeight : e.deltaY
}

/**
 * The scroll position a step of `dy` pixels should end at, and whether to animate
 * there. A step asked to animate does, unless the user prefers reduced motion; so
 * does any step while an earlier animated one is still under way — asked for less
 * than `SCROLL_CHAIN_MS` ago, not yet arrived, and the list still somewhere on its
 * path — and it starts from where THAT one was headed. Off its path, something
 * else has scrolled the list since (the list's own wheel, a drag, a script), and
 * continuing would jump back to a target nobody is heading for. Starting from the live position instead drops
 * whatever distance the animation had not covered yet, and an instant jump
 * cancels it outright: a trackpad fling (big deltas, then a tail of small ones)
 * stopped short and jerked.
 */
export function scrollStep(
  dy: number,
  view: { scrollTop: number; clientHeight: number; scrollHeight: number },
  aim: ScrollAim | null,
  now: number,
  animate: boolean,
  reducedMotion: boolean,
): { top: number; smooth: boolean; aim: ScrollAim | null } {
  const pending = aim !== null
    && now - aim.at < SCROLL_CHAIN_MS
    && Math.abs(view.scrollTop - aim.top) >= 1
    && view.scrollTop >= Math.min(aim.from, aim.top) - 1
    && view.scrollTop <= Math.max(aim.from, aim.top) + 1
  const from = pending ? aim.top : view.scrollTop
  const top = Math.max(0, Math.min(view.scrollHeight - view.clientHeight, from + dy))
  const smooth = !reducedMotion && (animate || pending)
  return { top, smooth, aim: smooth ? { from: view.scrollTop, top, at: now, last: view.scrollTop } : null }
}

/**
 * The pending step, after the list has scrolled to `scrollTop` — or null once the
 * list moves back against the step's heading. The path check in `scrollStep` sees
 * only where the list is, and a native scroll reversing ALONG the path (the list's
 * own wheel, back up toward where the step started) stays on it: the next step then
 * chained from a target nobody was heading for and jumped more than a page.
 */
export function followScroll(aim: ScrollAim | null, scrollTop: number): ScrollAim | null {
  if (!aim) return null
  const heading = Math.sign(aim.top - aim.from)
  if (heading !== 0 && (scrollTop - aim.last) * heading < -0.5) return null
  return aim.last === scrollTop ? aim : { ...aim, last: scrollTop }
}
